import { useState, useEffect } from 'react';
import { useToast } from '@chakra-ui/react';
import { useNavigate, useParams, Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useQuery } from 'hooks/useCustomQuery';
import { useQueryClient } from '@tanstack/react-query';
import { useMutation } from '@tanstack/react-query';

import { updateAppAuth, getAppAuthById } from 'API/AppAuth';
import {
    Alert,
    AlertDescription,
    AlertIcon,
    LoadingIndicator,
} from 'components/v4';

import FormBase from '@/components/forms/new/FormBase';

// Form field components
import AppAuthNameField from '../serviceprofiles/edit/formFields/AppAuthNameField';
import AppAuthTenantIDField from '../serviceprofiles/edit/formFields/AppAuthTenantIDField';
import AppAuthInitialDomainField from '../serviceprofiles/edit/formFields/AppAuthInitialDomainField';
import AppAuthManagedByField from '../serviceprofiles/edit/formFields/AppAuthManagedByField';
import AppAuthMaxAccessFailuresField from '../serviceprofiles/edit/formFields/AppAuthMaxAccessFailuresField';
import AppAuthAccessFailureWindowField from '../serviceprofiles/edit/formFields/AppAuthAccessFailureWindowField';
import AppAuthSelectiveSyncField from '../serviceprofiles/edit/formFields/AppAuthSelectiveSyncField';
import AppAuthUserSyncField from '../serviceprofiles/edit/formFields/AppAuthUserSyncField';

import AppAuthSelectiveSync from './AppAuthSelectiveSync';
import config from '@/config.json';

import useGetCurrentCompany from '@/queries/company/useGetCurrentCompany';
const DEFAULT_DATA = {
    name: 'App Auth',
    defaultManagedBy: 0,
    selectiveSync: false,
    maxAccessFailures: 0,
    accessFailureWindow: 0,
    userSync: false,
    tenantID: '',
    initialDomain: '',
    filterGroup: '',
    filterLocations: [],
};

/** App Auth component for Edit Services */
export default function AppAuth({ mode }) {
    const { id: serviceId } = useParams();
    const navigate = useNavigate();
    const toast = useToast();
    const queryClient = useQueryClient();
    const [error, setError] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const { currentCompany: companyId } = useSelector(
        (state) => state.navigationLists,
    );
    const { data: currentCompanyData } = useGetCurrentCompany();
    const { darkMode } = useSelector((state) => state.settings);
    const { requiredScope: accountScope } = useSelector((state) => state.login);

    const handleBackButton = () => navigate('/dashboard-services');

    // Initial data loading
    const [initialValues, setInitialValues] = useState(DEFAULT_DATA);

    const managedByOptions = config.users.managedBy;
    const selectiveSyncRequired = currentCompanyData?.selectiveSyncPolicy === 2;

    // Load service data for edit mode
    const {
        data: serviceData,
        isLoading: isLoadingService,
        error: queryError,
    } = useQuery({
        queryKey: ['appAuth', { id: serviceId }],
        queryFn: async () => {
            console.log('Fetching AppAuth data for serviceId:', serviceId);
            const res = await getAppAuthById(serviceId);
            console.log('API response:', res);
            return res.data;
        },
        enabled: !!serviceId && mode === 'edit',
        refetchOnWindowFocus: true,
        staleTime: 0, // Always consider data stale
        cacheTime: 0, // Don't cache data
    });

    // Update initial values when service data is loaded
    useEffect(() => {
        if (serviceData) {
            console.log('Service data loaded:', serviceData);
            setInitialValues({
                ...serviceData,
                name: serviceData.name || 'App Auth',
                defaultManagedBy: serviceData.defaultManagedBy || 0,
                selectiveSync: serviceData.selectiveSync || false,
                maxAccessFailures: serviceData.maxAccessFailures || 0,
                accessFailureWindow: serviceData.accessFailureWindow || 0,
                userSync: serviceData.userSync || false,
                tenantID: serviceData.tenantID || '',
                initialDomain: serviceData.initialDomain || '',
                filterGroup: serviceData.filterGroup || '',
                filterLocations: serviceData.filterLocations || [],
            });
        }
    }, [serviceData]);

    // Debug logging
    useEffect(() => {
        console.log('=== AppAuth Debug Info ===');
        console.log('serviceId:', serviceId);
        console.log('mode:', mode);
        console.log('serviceId exists:', !!serviceId);
        console.log('mode is edit:', mode === 'edit');
        console.log('Query enabled condition:', !!serviceId && mode === 'edit');
        console.log('Loading state:', isLoadingService);
        console.log('Service data:', serviceData);
        if (queryError) {
            console.error('Query error:', queryError);
        }
        console.log('========================');
    }, [serviceId, mode, isLoadingService, queryError, serviceData]);

    const updateManaged = useMutation({
        mutationFn: (payload) => updateAppAuth(payload, accountScope),
        onSuccess: () => {
            toast({
                title: 'Success',
                description: 'App Auth updated successfully',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
            // Invalidate and refetch the query to ensure fresh data on next visit
            queryClient.invalidateQueries(['appAuth', { id: serviceId }]);
            // Also invalidate related queries
            queryClient.invalidateQueries(['appAuth']);
            navigate('/dashboard-services');
        },
        onError: (err) => {
            setError(err);
        },
    });

    const handleSubmit = async (values) => {
        const payload = {
            ...values,
            id: serviceId,
            maxAccessFailures: Number(values.maxAccessFailures),
            accessFailureWindow: Number(values.accessFailureWindow),
        };

        setIsLoading(true);
        try {
            updateManaged.mutate(payload);
        } catch (e) {
            setError(e);
        } finally {
            setIsLoading(false);
        }
    };

    const breadCrumbTrail = [
        { label: 'Services', path: ['/dashboard-services', { index: 0 }] },
        {
            label: `${mode === 'add' ? 'Add' : 'Edit'} App Auth Service`,
            path: '',
        },
    ];

    // Check if user has access to this service
    if (
        mode === 'edit' &&
        initialValues.companyID &&
        initialValues.companyID !== companyId
    ) {
        return <Navigate to="/dashboard-services" />;
    }

    // Only show loading spinner for edit mode when actually loading service data
    if (mode === 'edit' && isLoadingService) {
        return <LoadingIndicator />;
    }

    return (
        <FormBase
            breadCrumbTrail={breadCrumbTrail}
            header={`${mode === 'add' ? 'Add' : 'Editing'} App Auth`}
            initialValues={initialValues}
            finalFormSubmit={handleSubmit}
            customClick={true}
            onBack={handleBackButton}
            enableReinitialize={true}
            isSubmitDisabled={isLoading}>
            {({ values, setFieldValue, errors, touched }) =>
                [
                    <AppAuthTenantIDField
                        key="tenantID"
                        darkMode={darkMode}
                        errors={errors}
                        touched={touched}
                        values={values}
                    />,
                    <AppAuthInitialDomainField
                        key="initialDomain"
                        darkMode={darkMode}
                        errors={errors}
                        touched={touched}
                    />,
                    <AppAuthManagedByField
                        key="defaultManagedBy"
                        field={{
                            name: 'defaultManagedBy',
                            value: values.defaultManagedBy,
                        }}
                        form={{ values, setFieldValue, errors, touched }}
                        options={managedByOptions}
                        darkMode={darkMode}
                    />,
                    // Only show these fields if user has higher account scope
                    ...(accountScope > 20
                        ? [
                              <AppAuthMaxAccessFailuresField
                                  key="maxAccessFailures"
                                  darkMode={darkMode}
                                  errors={errors}
                                  touched={touched}
                              />,
                              <AppAuthAccessFailureWindowField
                                  key="accessFailureWindow"
                                  darkMode={darkMode}
                                  errors={errors}
                                  touched={touched}
                              />,
                          ]
                        : []),
                    <AppAuthUserSyncField
                        key="userSync"
                        value={values.userSync}
                        onChange={(checked) =>
                            setFieldValue('userSync', checked)
                        }
                        darkMode={darkMode}
                    />,
                    <AppAuthSelectiveSyncField
                        key="selectiveSync"
                        onChange={(checked) => {
                            setFieldValue('selectiveSync', checked);
                            // Reset selective sync form when toggled
                            if (!checked) {
                                setFieldValue('filterGroup', '');
                                setFieldValue('filterLocations', []);
                            }
                        }}
                        isDisabled={
                            currentCompanyData?.selectiveSyncPolicy === 0 ||
                            selectiveSyncRequired
                        }
                        isChecked={
                            values.selectiveSync || selectiveSyncRequired
                        }
                        darkMode={darkMode}
                    />,

                    // Show selective sync component when enabled
                    ...(values.selectiveSync || selectiveSyncRequired
                        ? [
                              <AppAuthSelectiveSync
                                  key="selectiveSync"
                                  formValues={values}
                                  handleInputChange={(e) => {
                                      const { name, value } = e.target;
                                      setFieldValue(name, value);
                                  }}
                                  setFormValues={(updater) => {
                                      const newValues =
                                          typeof updater === 'function'
                                              ? updater(values)
                                              : updater;
                                      Object.keys(newValues).forEach((key) => {
                                          setFieldValue(key, newValues[key]);
                                      });
                                  }}
                              />,
                          ]
                        : []),
                    error && (
                        <Alert
                            key="error"
                            status="error"
                            sx={{ marginTop: '1rem' }}>
                            <AlertIcon />
                            <AlertDescription>
                                {error?.response?.data?.message ||
                                    'An error occurred. Please try again later.'}
                            </AlertDescription>
                        </Alert>
                    ),
                ].filter(Boolean)
            }
        </FormBase>
    );
}
